import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, Download, Copy, Eye, Ban, FileText, Inbox, Undo2, Plane } from "lucide-react";
import { RefundDialog } from "@/components/RefundDialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/AdminLayout";
import { StatusBadge } from "@/components/StatusBadge";
import { ChargeDetailSheet } from "@/components/ChargeDetailSheet";
import { PreviewDialog } from "@/components/PreviewDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useManualReview, ManualReviewPanel } from "@/components/ManualReview";
import { useColumnPrefs, ResizeHandle, ColumnPicker } from "@/components/ColumnTools";
import { STATUS, CURRENCIES, fmtMoney, fmtDate, fmtDateTime, orNA, payLink, chargeLink, copyText, chargeToTemplate } from "@/lib/format";

const FilterSelect = ({ value, onChange, placeholder, items, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-full bg-white sm:w-44" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{placeholder}</SelectItem>
      {items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
    </SelectContent>
  </Select>
);

const CashierSummary = ({ rows, name }) => {
  const paid = rows.filter((c) => c.payment_status === "pagado");
  const totals = CURRENCIES.map((cur) => [cur, paid.filter((c) => c.currency === cur).reduce((s, c) => s + (c.amount_cents || 0), 0)]).filter(([, v]) => v > 0);
  const pending = rows.filter((c) => c.link_status === "activo" && c.payment_status !== "pagado").length;
  const stat = (label, value, testid) => (
    <div><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p><p className="mt-1 font-heading text-2xl font-semibold text-slate-900" data-testid={testid}>{value}</p></div>
  );
  return (
    <div className="mb-5 grid gap-5 rounded-2xl border border-[#2F5BFF]/20 bg-[#2F5BFF]/5 p-5 sm:grid-cols-4" data-testid="cashier-summary">
      {stat("Cajero", name, "cashier-summary-name")}
      {stat("Cobrado", totals.length ? totals.map(([cur, v]) => fmtMoney(v, cur)).join(" · ") : fmtMoney(0, "USD"), "cashier-summary-total")}
      {stat("Pagos confirmados", paid.length, "cashier-summary-paid")}
      {stat("Enlaces activos sin pagar", pending, "cashier-summary-pending")}
    </div>
  );
};

const COLS = [
  { key: "hotel", label: "Plantilla", width: 230, render: (c, h) => <><span className="flex items-center gap-2"><span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: h?.primary_color }} /><span className="truncate">{h?.name || c.hotel_slug}</span></span>{c.property_name && <span className="mt-0.5 block truncate pl-[18px] text-xs text-slate-500" title={c.property_name} data-testid={`charge-property-${c.id}`}>{c.property_name}</span>}</> },
  { key: "guest", label: "Huésped", width: 230, render: (c) => <><p className="flex items-center gap-1.5 font-medium"><span className="truncate" title={c.guest_name}>{orNA(c.guest_name)}</span>{(c.flights?.outbound?.length || c.flights?.inbound?.length) ? <span title="Incluye vuelos" className="inline-flex rounded-full bg-sky-100 p-1 text-sky-700" data-testid={`charge-flights-icon-${c.id}`}><Plane size={12} /></span> : null}</p><p className="truncate text-xs text-slate-500" title={c.email}>{orNA(c.email)}</p></> },
  { key: "stay", label: "Estancia", width: 130, className: "text-xs text-slate-600", render: (c) => (c.check_in || c.check_out ? <>{fmtDate(c.check_in)} → {fmtDate(c.check_out)}</> : "Sin fechas") },
  { key: "amount", label: "Monto", width: 130, className: "font-mono font-semibold", render: (c) => fmtMoney(c.amount_cents, c.currency) },
  { key: "processor", label: "Procesador", width: 140, render: (c) => orNA(c.processor_name) },
  { key: "cashier", label: "Cajero", width: 150, className: "text-slate-700", testid: "charge-cashier", render: (c) => orNA(c.created_by_name) },
  { key: "status", label: "Estado", width: 140, render: (c) => <StatusBadge status={c.status} testid={`charge-status-${c.id}`} /> },
  { key: "created", label: "Creado", width: 140, className: "text-xs text-slate-500", render: (c) => fmtDateTime(c.created_at) },
];
const ACTIONS_W = 210;

export default function Charges() {
  const [hotels, setHotels] = useState([]);
  const [processors, setProcessors] = useState([]);
  const [rows, setRows] = useState(null);
  const [f, setF] = useState({ q: "", hotel: "all", processor: "all", status: "all", currency: "all", flights: "all", cashier: "all", group: "all", date_from: "", date_to: "" });
  const [creators, setCreators] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [detail, setDetail] = useState(null);
  const [preview, setPreview] = useState(null);
  const [toCancel, setToCancel] = useState(null);
  const [toRefund, setToRefund] = useState(null);
  const [reviewKey, setReviewKey] = useState(0);
  const { user } = useAuth();
  const review = useManualReview(user?.role === "admin", false, reviewKey);
  const cols = useColumnPrefs("charges-table-cols", COLS);
  const tableW = cols.shown.reduce((s, c) => s + cols.widths[c.key], ACTIONS_W);

  useEffect(() => {
    api.get("/hotels").then((r) => setHotels(r.data));
    api.get("/processors").then((r) => setProcessors(r.data));
    api.get("/charges/creators").then((r) => setCreators(r.data));
    if (user?.role === "admin") api.get("/groups").then((r) => setAllGroups(r.data));
  }, []);

  const params = useCallback(() => {
    const p = {};
    Object.entries(f).forEach(([k, v]) => { if (v && v !== "all") p[k] = v; });
    return p;
  }, [f]);

  const load = useCallback(() => {
    api.get("/charges", { params: params() }).then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e).message));
  }, [params]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => { clearTimeout(t); };
  }, [load]);

  const hotelOf = (slug) => hotels.find((h) => h.slug === slug);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));

  const exportCsv = async () => {
    try {
      const r = await api.get("/charges/export.csv", { params: params(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `cobros-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("No se pudo exportar"); }
  };

  const doCancel = async () => {
    try { await api.post(`/charges/${toCancel.id}/cancel`); toast.success("Cobro cancelado"); load(); }
    catch (e) { toast.error(apiError(e).message); }
  };

  const showPreview = (c) => { const h = hotelOf(c.hotel_slug); if (h) setPreview({ hotel: h, data: chargeToTemplate(c) }); };

  return (
    <>
      <PageHeader title="Reservaciones y cobros" subtitle="Busca, filtra y da seguimiento a cada enlace y transacción."
        actions={<div className="flex flex-wrap gap-2"><ColumnPicker columns={COLS} prefs={cols} /><Button variant="outline" data-testid="btn-export-csv" onClick={exportCsv}><Download size={16} className="mr-2" />Exportar CSV</Button></div>} />
      <ManualReviewPanel items={review} onOpen={setDetail} />
      <div className="mb-5 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 lg:flex-row lg:flex-wrap lg:items-center">
        <div className="relative flex-1 lg:min-w-[260px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder="Buscar por huésped, correo o plantilla" value={f.q} onChange={set("q")} data-testid="reservations-search-input" />
        </div>
        <FilterSelect value={f.hotel} onChange={set("hotel")} placeholder="Todas las plantillas" testid="reservations-filter-hotel" items={hotels.map((h) => [h.slug, h.name])} />
        <FilterSelect value={f.processor} onChange={set("processor")} placeholder="Todos los procesadores" testid="reservations-filter-processor" items={processors.map((p) => [p.id, p.name])} />
        <FilterSelect value={f.status} onChange={set("status")} placeholder="Todos los estados" testid="reservations-filter-status" items={Object.entries(STATUS).map(([k, v]) => [k, v.label])} />
        <FilterSelect value={f.currency} onChange={set("currency")} placeholder="Todas las monedas" testid="reservations-filter-currency" items={CURRENCIES.map((c) => [c, c])} />
        <FilterSelect value={f.flights} onChange={set("flights")} placeholder="Con y sin vuelos" testid="reservations-filter-flights" items={[["yes", "Con vuelos"], ["no", "Sin vuelos"]]} />
        {user?.role !== "cashier" && <FilterSelect value={f.cashier} onChange={set("cashier")} placeholder="Todos los cajeros" testid="reservations-filter-cashier" items={creators.map((c) => [c.email, c.name])} />}
        {user?.role === "admin" && <FilterSelect value={f.group} onChange={set("group")} placeholder="Todos los grupos" testid="reservations-filter-group" items={[["none", "Sin grupo asignado"], ...allGroups.map((g) => [g.id, g.name])]} />}
        <div className="flex items-center gap-2">
          <Input type="date" className="w-full min-w-0 flex-1 sm:w-40 sm:flex-none" value={f.date_from} onChange={set("date_from")} data-testid="reservations-filter-date-from" aria-label="Desde" />
          <span className="text-xs text-slate-400">a</span>
          <Input type="date" className="w-full min-w-0 flex-1 sm:w-40 sm:flex-none" value={f.date_to} onChange={set("date_to")} data-testid="reservations-filter-date-to" aria-label="Hasta" />
        </div>
      </div>

      {f.cashier !== "all" && rows && <CashierSummary rows={rows} name={creators.find((c) => c.email === f.cashier)?.name || f.cashier} />}
      {rows && rows.length === 0 ? (
        <div className="flex flex-col items-center rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center" data-testid="charges-empty-state">
          <Inbox size={36} className="text-slate-300" />
          <p className="mt-4 font-heading text-lg font-semibold">Sin registros</p>
          <p className="mt-1 max-w-sm text-sm text-slate-500">No hay cobros que coincidan con los filtros. Crea uno nuevo para empezar.</p>
          <Button asChild className="mt-5 bg-[#2F5BFF] hover:bg-[#1F45E0]"><Link to="/panel/nuevo">Nuevo cobro</Link></Button>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="table-fixed text-sm" style={{ width: tableW, minWidth: "100%" }} data-testid="charges-table">
            <colgroup>{cols.shown.map((c) => <col key={c.key} style={{ width: cols.widths[c.key] }} />)}<col style={{ width: ACTIONS_W }} /></colgroup>
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                {cols.shown.map((c) => (
                  <th key={c.key} className="relative truncate px-4 py-3 font-semibold" data-testid={`col-header-${c.key}`}>
                    {c.label}<ResizeHandle colKey={c.key} width={cols.widths[c.key]} onResize={cols.resize} />
                  </th>
                ))}
                <th className="right-0 bg-slate-50 sm:sticky px-4 py-3 text-right font-semibold shadow-[-8px_0_12px_-10px_rgba(15,23,42,0.25)]">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {(rows || []).map((c) => {
                const h = hotelOf(c.hotel_slug);
                return (
                  <tr key={c.id} className="group border-b border-slate-100 transition-colors hover:bg-slate-50" data-testid={`charge-row-${c.id}`}>
                    {cols.shown.map((col) => (
                      <td key={col.key} className={`overflow-hidden break-words px-4 py-3 ${col.className || ""}`} data-testid={col.testid ? `${col.testid}-${c.id}` : undefined}>{col.render(c, h)}</td>
                    ))}
                    <td className="right-0 bg-white sm:sticky px-4 py-3 shadow-[-8px_0_12px_-10px_rgba(15,23,42,0.25)] transition-colors group-hover:bg-slate-50">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" title="Detalle" data-testid={`btn-detail-${c.id}`} onClick={() => setDetail(c.id)}><FileText size={16} /></Button>
                        <Button size="icon" variant="ghost" title="Vista previa" data-testid={`btn-preview-${c.id}`} onClick={() => showPreview(c)}><Eye size={16} /></Button>
                        <Button size="icon" variant="ghost" title="Copiar enlace" disabled={!c.token || c.link_status !== "activo"} data-testid={`btn-copy-link-${c.id}`}
                          onClick={async () => { if (await copyText(chargeLink(c))) toast.success("Enlace copiado"); }}><Copy size={16} /></Button>
                        <Button size="icon" variant="ghost" title="Cancelar" className="text-rose-600" disabled={!["borrador", "activo"].includes(c.link_status) || c.payment_status === "pagado" || (user?.role === "agent" && c.created_by !== user?.email)}
                          data-testid={`btn-cancel-payment-${c.id}`} onClick={() => setToCancel(c)}><Ban size={16} /></Button>
                        {["admin", "manager"].includes(user?.role) && c.payment_status === "pagado" && (
                          <Button size="icon" variant="ghost" title="Reembolsar" className="text-violet-600" data-testid={`btn-refund-${c.id}`} onClick={() => setToRefund(c)}><Undo2 size={16} /></Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!rows && <tr><td colSpan={cols.shown.length + 1} className="px-4 py-10 text-center text-slate-400">Cargando…</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <ChargeDetailSheet chargeId={detail} onOpenChange={(o) => { if (!o) setDetail(null); }} hotels={hotels} onChanged={() => { load(); setReviewKey((k) => k + 1); }} onPreview={showPreview} />
      <PreviewDialog open={!!preview} onOpenChange={() => setPreview(null)} hotel={preview?.hotel} data={preview?.data} />
      <ConfirmDialog open={!!toCancel} onOpenChange={(o) => { if (!o) setToCancel(null); }} title="¿Cancelar este cobro?" confirmLabel="Cancelar cobro"
        description="El enlace dejará de funcionar y el cliente no podrá pagar." onConfirm={doCancel} />
      <RefundDialog charge={toRefund} onClose={() => setToRefund(null)} onDone={load} />
    </>
  );
}
